import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { bulkTag, createSegment, deleteSegment, fetchSegments, fetchTags, type Segment } from './tagsApi'
import type { FilterSpec } from './types'

/**
 * Пометка выбранных строк тегом (тикет #105).
 *
 * Тег вводится словом, а не выбирается из списка обязательно: новый тег заводится тут же,
 * иначе пометка требовала бы похода к администратору. Подсказки — из справочника.
 * Результат называет, сколько записей изменилось на самом деле: право на правку бывает уже
 * права на чтение, которым нарисован список, и «помечено 3» при двух помеченных — неправда.
 */
export function TagBulkActions({
  entity,
  ids,
  onDone,
}: {
  entity: string
  ids: string[]
  onDone: (message: string) => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const tags = useQuery({ queryKey: ['tags'], queryFn: ({ signal }) => fetchTags(signal) })
  const mutation = useMutation({
    mutationFn: (remove: boolean) => bulkTag(entity, ids, name.trim(), remove),
    onSuccess: (result, remove) => {
      setName('')
      onDone(
        t(remove ? 'tags.removed' : 'tags.added', {
          tag: result.tagName,
          affected: result.affected,
          requested: result.requested,
        }),
      )
      void queryClient.invalidateQueries({ queryKey: ['list', entity] })
      void queryClient.invalidateQueries({ queryKey: ['tags'] })
      void queryClient.invalidateQueries({ queryKey: ['segments', entity] })
    },
    onError: (error) => onDone((error as Error).message),
  })
  const listId = `tag-options-${entity}`
  return (
    <span className="report-actions">
      <input
        aria-label={t('tags.input')}
        placeholder={t('tags.input')}
        list={listId}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <datalist id={listId}>
        {(tags.data ?? []).map((tag) => (
          <option key={tag.id} value={tag.name} />
        ))}
      </datalist>
      <button type="button" disabled={!name.trim() || mutation.isPending} onClick={() => mutation.mutate(false)}>
        {t('tags.add')}
      </button>
      <button type="button" disabled={!name.trim() || mutation.isPending} onClick={() => mutation.mutate(true)}>
        {t('tags.remove')}
      </button>
    </span>
  )
}

/**
 * Сегменты списка (тикет #105): именованный фильтр со счётчиком.
 *
 * Счётчик считает сервер при каждом чтении и в области видимости смотрящего — поэтому он
 * обновляется вместе с данными, а не замирает на момент сохранения. Нажатие на сегмент
 * подставляет его фильтр в список: список и сегмент считают одно и то же одним механизмом.
 */
export function SegmentBar({
  entity,
  filters,
  onApply,
}: {
  entity: string
  filters: FilterSpec[]
  onApply: (filters: FilterSpec[]) => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<{ name: string; shared: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const segments = useQuery({
    queryKey: ['segments', entity],
    queryFn: ({ signal }) => fetchSegments(entity, signal),
    retry: false,
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['segments', entity] })
  const save = useMutation({
    mutationFn: () => createSegment({ name: draft?.name ?? '', entity, filters, shared: draft?.shared ?? false }),
    onSuccess: () => {
      setDraft(null)
      setError(null)
      refresh()
    },
    onError: (failure) => setError((failure as Error).message),
  })
  const remove = useMutation({ mutationFn: deleteSegment, onSuccess: refresh, onError: (f) => setError((f as Error).message) })

  return (
    <div className="report-actions" aria-label={t('segments.title')} role="group">
      <span className="muted">{t('segments.title')}:</span>
      {(segments.data ?? []).map((segment: Segment) => (
        <span key={segment.id}>
          <button type="button" onClick={() => onApply(segment.filters)}
                  aria-label={`${t('segments.apply')} ${segment.name}`}>
            {segment.name} ({segment.count})
          </button>
          {segment.mine && (
            <button type="button" aria-label={`${t('segments.delete')} ${segment.name}`}
                    disabled={remove.isPending} onClick={() => remove.mutate(segment.id)}>
              ×
            </button>
          )}
        </span>
      ))}
      {draft ? (
        <form
          className="report-actions"
          aria-label={t('segments.form')}
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <input aria-label={t('segments.name')} placeholder={t('segments.name')} value={draft.name}
                 onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          <label>
            <input type="checkbox" checked={draft.shared}
                   onChange={(event) => setDraft({ ...draft, shared: event.target.checked })} />{' '}
            {t('segments.shared')}
          </label>
          <button type="submit" disabled={!draft.name.trim() || save.isPending}>{t('segments.save')}</button>
          <button type="button" onClick={() => setDraft(null)}>{t('admin.cancel')}</button>
        </form>
      ) : (
        // A segment without a filter is "everyone": allowed, but it should be a choice,
        // so the button says what it saves.
        <button type="button" onClick={() => setDraft({ name: '', shared: false })}>
          {filters.length > 0 ? t('segments.saveCurrent') : t('segments.saveAll')}
        </button>
      )}
      {error && <span role="alert">{error}</span>}
    </div>
  )
}
